/**
 * 用途：Google Ads 广告洞察页，展示 Google 账户维度的广告花费、展示量、点击量等效果指标
 * 路由：/ad-analysis/gg-insights
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ggInsightsApi, type GgInsightRow, type AccountOption, type TagFilterGroup } from '../api/ggInsights';
import useAppStore from '../store/useAppStore';
import { MultiSelect, type SelectOption } from '../components/MultiSelect';
import Pagination from '../components/Pagination';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import { ColumnToggle } from '../components/ColumnToggle';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';
import { getCurrentCalendarRange, MARKETING_RANGE_PRESETS, MarketingDateRangePicker } from '../components/MarketingTimeRangeFilter';

// ── Metric field definitions ────────────────────────────────────────────────

interface MetricField {
  key: keyof GgInsightRow;
  label: string;
  group: string;
  fmt: (v: number) => string;
  permKey?: FieldKey;
}

const fmtNum = (v: number, d = 2) => v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtInt = (v: number) => v == null ? '-' : v.toLocaleString('en-US');
const fmtPct = (v: number) => v == null ? '-' : (v * 100).toFixed(2) + '%';

const ALL_METRICS: MetricField[] = [
  // basic
  { key: 'spend', label: '花费', group: '基础指标', fmt: v => fmtNum(v), permKey: 'spend' },
  { key: 'impressions', label: '展示', group: '基础指标', fmt: fmtInt },
  { key: 'clicks', label: '点击', group: '基础指标', fmt: fmtInt },
  { key: 'ctr', label: 'CTR', group: '基础指标', fmt: fmtPct },
  { key: 'average_cpc', label: 'CPC', group: '基础指标', fmt: v => fmtNum(v), permKey: 'cpc' },
  { key: 'average_cpm', label: 'CPM', group: '基础指标', fmt: v => fmtNum(v), permKey: 'cpm' },
  { key: 'purchase_count', label: '购买数', group: '购买指标', fmt: v => fmtNum(v) },
  { key: 'purchase_value', label: '购买金额', group: '购买指标', fmt: v => fmtNum(v), permKey: 'revenue' },
  { key: 'roas', label: 'ROAS', group: '转化指标', fmt: v => fmtNum(v), permKey: 'roas' },
  { key: 'cost_per_purchase', label: '单次购买成本', group: '购买指标', fmt: v => fmtNum(v), permKey: 'cpa' },
  { key: 'interactions', label: '互动', group: '基础指标', fmt: fmtInt },
  { key: 'interaction_rate', label: '互动率', group: '基础指标', fmt: fmtPct },
  { key: 'engagements', label: '参与度', group: '基础指标', fmt: fmtInt },
  { key: 'engagement_rate', label: '参与率', group: '基础指标', fmt: fmtPct },
  // conversion
  { key: 'conversions_from_interactions_rate', label: '转化率', group: '转化指标', fmt: fmtPct },
  { key: 'view_through_conversions', label: '浏览型转化', group: '转化指标', fmt: fmtInt },
  // all conversions
  { key: 'all_conversions', label: '所有转化', group: '所有转化', fmt: v => fmtNum(v) },
  { key: 'all_conversions_value', label: '所有转化价值', group: '所有转化', fmt: v => fmtNum(v), permKey: 'revenue' },
  { key: 'cost_per_all_conversions', label: '所有转化单次费用', group: '所有转化', fmt: v => fmtNum(v), permKey: 'cpa' },
  { key: 'all_conversions_from_interactions_rate', label: '所有转化率', group: '所有转化', fmt: fmtPct },
  { key: 'value_per_all_conversions', label: '所有转化单次价值', group: '所有转化', fmt: v => fmtNum(v), permKey: 'revenue' },
  // position
  { key: 'top_impression_percentage', label: '页首展示份额', group: '展示份额', fmt: fmtPct },
];

const DEFAULT_FIELDS = ['spend', 'impressions', 'clicks', 'ctr', 'average_cpc', 'average_cpm', 'purchase_count', 'purchase_value', 'roas', 'cost_per_purchase'];
const DIMENSION_OPTIONS: SelectOption[] = [
  { value: '', label: '不细分' },
  { value: 'age', label: '年龄' },
  { value: 'gender', label: '性别' },
  { value: 'device', label: '设备' },
  { value: 'network', label: '广告网络' },
  { value: 'ad_format', label: '广告格式' },
  { value: 'audience', label: '受众' },
  { value: 'click_type', label: '点击类型' },
  { value: 'conversion', label: '转化操作' },
  { value: 'conversion_category', label: '转化类别' },
  { value: 'income', label: '收入' },
  { value: 'keyword', label: '关键词' },
  { value: 'parental_status', label: '父母状态' },
  { value: 'slot', label: '广告位' },
];

function tagGroupToOpts(group: TagFilterGroup): SelectOption[] {
  return [
    { value: '', label: `全部${group.label}` },
    ...group.options.map(o => ({ value: o.value, label: o.label })),
  ];
}

// ── Subtotal helpers ────────────────────────────────────────────────────────

const ADDITIVE_KEYS: (keyof GgInsightRow)[] = [
  'spend', 'impressions', 'clicks',
  'conversions', 'conversions_value', 'all_conversions', 'all_conversions_value',
  'interactions', 'view_through_conversions', 'engagements',
  'purchase_count', 'purchase_value',
];

function computeSubtotal(rows: GgInsightRow[]): GgInsightRow {
  const sub = { date: '' } as GgInsightRow;
  for (const key of ADDITIVE_KEYS) {
    (sub as any)[key] = rows.reduce((s, r) => s + ((r[key] as number) || 0), 0);
  }
  sub.ctr = sub.impressions > 0 ? sub.clicks / sub.impressions : 0;
  sub.average_cpc = sub.clicks > 0 ? sub.spend / sub.clicks : 0;
  sub.average_cpm = sub.impressions > 0 ? (sub.spend / sub.impressions) * 1000 : 0;
  sub.roas = sub.spend > 0 ? sub.conversions_value / sub.spend : 0;
  sub.cost_per_conversion = sub.conversions > 0 ? sub.spend / sub.conversions : 0;
  sub.conversions_from_interactions_rate = sub.clicks > 0 ? sub.conversions / sub.clicks : 0;
  sub.value_per_conversion = sub.conversions > 0 ? sub.conversions_value / sub.conversions : 0;
  sub.cost_per_all_conversions = sub.all_conversions > 0 ? sub.spend / sub.all_conversions : 0;
  sub.all_conversions_from_interactions_rate = sub.clicks > 0 ? sub.all_conversions / sub.clicks : 0;
  sub.value_per_all_conversions = sub.all_conversions > 0 ? sub.all_conversions_value / sub.all_conversions : 0;
  sub.interaction_rate = sub.impressions > 0 ? sub.interactions / sub.impressions : 0;
  sub.engagement_rate = sub.impressions > 0 ? sub.engagements / sub.impressions : 0;
  sub.purchase_roas = sub.spend > 0 ? sub.purchase_value / sub.spend : 0;
  sub.cost_per_purchase = sub.purchase_count > 0 ? sub.spend / sub.purchase_count : 0;
  return sub;
}

type DatePreset = 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'thisWeek' | 'thisMonth' | 'custom';

function daysAgo(n: number): string {
  const d = new Date(); d.setDate(d.getDate() - (n - 1));
  return d.toISOString().slice(0, 10);
}
function today(): string { return new Date().toISOString().slice(0, 10); }

// ── Component ───────────────────────────────────────────────────────────────

export default function GgInsights() {
  const { brandId, siteId, adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [customStart, setCustomStart] = useState(() => daysAgo(7));
  const [customEnd, setCustomEnd] = useState(() => today());
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [dimension, setDimension] = useState('');
  const [adId, setAdId] = useState('');
  const [adName, setAdName] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [adGroupName, setAdGroupName] = useState('');
  const [tagFilterValues, setTagFilterValues] = useState<Record<string, string>>({});
  const [tagFilters, setTagFilters] = useState<TagFilterGroup[]>([]);
  const [visibleFields, setVisibleFields] = useState<string[]>(DEFAULT_FIELDS);
  const [hiddenMetricKeys, setHiddenMetricKeys] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('gg_insights_hidden_cols');
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      // ignore
    }
    return new Set(ALL_METRICS.map(m => m.key as string).filter(key => !DEFAULT_FIELDS.includes(key)));
  });

  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [data, setData] = useState<GgInsightRow[]>([]);
  const [summary, setSummary] = useState<GgInsightRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    if (!brandId) return;
    ggInsightsApi.getAccounts({ brand_id: brandId, site_id: siteId || undefined, scope: 'dashboard_read' })
      .then(res => setAccounts(res.data ?? []))
      .catch(() => setAccounts([]));
    ggInsightsApi.getTagOptions({ brand_id: brandId })
      .then(res => setTagFilters(res.data ?? []))
      .catch(() => setTagFilters([]));
    setSelectedAccounts([]);
    setTagFilterValues({});
  }, [brandId, siteId]);

  const getDateRange = useCallback(() => {
    if (datePreset === 'thisWeek' || datePreset === 'thisMonth') return getCurrentCalendarRange(datePreset, today());
    if (datePreset === 'today') return { start: today(), end: today() };
    if (datePreset === 'yesterday') return { start: daysAgo(1), end: daysAgo(1) };
    if (datePreset === '7d') return { start: daysAgo(7), end: today() };
    if (datePreset === '14d') return { start: daysAgo(14), end: today() };
    if (datePreset === '30d') return { start: daysAgo(30), end: today() };
    return { start: customStart, end: customEnd };
  }, [datePreset, customStart, customEnd]);

  const fetchData = useCallback(async (p = 1) => {
    const { start, end } = getDateRange();
    setLoading(true);
    try {
      const tagParams: Record<string, string | undefined> = {};
      for (const [k, v] of Object.entries(tagFilterValues)) {
        if (v) tagParams[k] = v;
      }
      const res = await ggInsightsApi.getList({
        brand_id: brandId,
        site_id: siteId || undefined,
        start_date: start,
        end_date: end,
        account_ids: selectedAccounts.length > 0 ? selectedAccounts.join(',') : undefined,
        dimension: dimension || undefined,
        ad_id: adId || undefined,
        ad_name: adName || undefined,
        campaign_name: campaignName || undefined,
        ad_group_name: adGroupName || undefined,
        ...tagParams,
        page: p,
        size: pageSize,
      });
      const d = res.data;
      setData(d.list ?? []);
      setSummary(d.summary ?? null);
      setTotal(d.total);
      setPage(d.page);
      setTotalPage(d.total_page);
    } catch (e) {
      console.error('Failed to load gg insights:', e);
      setData([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [brandId, siteId, getDateRange, selectedAccounts, dimension, adId, adName, campaignName, adGroupName, tagFilterValues, pageSize]);

  useEffect(() => { fetchData(1); }, [fetchData]);

  const handlePageChange = (p: number) => fetchData(p);

  const activeMetrics = useMemo(() => ALL_METRICS.filter(m => visibleFields.includes(m.key as string)), [visibleFields]);
  const metricToggleCols = useMemo(() => ALL_METRICS.map(m => ({ key: m.key as string, label: m.label })), []);
  const toggleMetricHidden = useCallback((key: string) => {
    setHiddenMetricKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem('gg_insights_hidden_cols', JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);
  const resetMetricHidden = useCallback(() => {
    setHiddenMetricKeys(new Set());
    try { localStorage.removeItem('gg_insights_hidden_cols'); } catch {}
  }, []);

  useEffect(() => {
    setVisibleFields(
      ALL_METRICS.map(m => m.key as string).filter(key => !hiddenMetricKeys.has(key)),
    );
  }, [hiddenMetricKeys]);

  const hasAccount = selectedAccounts.length > 0;
  const hasDimension = dimension !== '';
  const dimLabel = DIMENSION_OPTIONS.find(o => o.value === dimension)?.label ?? '';

  const dailySubtotals = useMemo(() => {
    if (!hasAccount && !hasDimension) return new Map<string, GgInsightRow>();
    const groups = new Map<string, GgInsightRow[]>();
    for (const row of data) {
      const date = row.date;
      if (!groups.has(date)) groups.set(date, []);
      groups.get(date)!.push(row);
    }
    const subtotals = new Map<string, GgInsightRow>();
    for (const [date, rows] of groups) {
      if (rows.length > 1) subtotals.set(date, computeSubtotal(rows));
    }
    return subtotals;
  }, [data, hasAccount, hasDimension]);

  const rowSpans = useMemo(() => {
    const dateSpan: number[] = new Array(data.length).fill(1);
    const accountSpan: number[] = new Array(data.length).fill(1);
    const needDateMerge = hasAccount || hasDimension;
    const needAccountMerge = hasAccount && hasDimension;
    if (!needDateMerge) return { dateSpan, accountSpan };

    let i = 0;
    while (i < data.length) {
      let j = i + 1;
      while (j < data.length && data[j].date === data[i].date) j++;
      dateSpan[i] = j - i;
      for (let k = i + 1; k < j; k++) dateSpan[k] = 0;
      if (needAccountMerge) {
        let ai = i;
        while (ai < j) {
          let aj = ai + 1;
          while (aj < j && data[aj].account_id === data[ai].account_id) aj++;
          accountSpan[ai] = aj - ai;
          for (let k = ai + 1; k < aj; k++) accountSpan[k] = 0;
          ai = aj;
        }
      }
      i = j;
    }
    return { dateSpan, accountSpan };
  }, [data, hasAccount, hasDimension]);

  const accountOptions: SelectOption[] = useMemo(
    () => accounts.map(a => ({ value: a.account_id, label: a.account_name || a.account_id })),
    [accounts],
  );

  const mkFilter = (label: string, opts: SelectOption[], value: string, onChange: (v: string) => void, minW = 120) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: minW }}>
      <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>{label}</label>
      <MultiSelect single options={opts} value={value ? [value] : ['']} onChange={v => onChange(v[0] === '' ? '' : v[0] ?? '')} placeholder="全部" />
    </div>
  );

  return (
    <div className="page-section visible">
      {/* ── Date Row ────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px',
        background: 'var(--surface)', borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border2)', marginBottom: 12,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap' }}>日期范围</span>
        <div className="page-tabs" style={{ marginBottom: 0 }}>
          {MARKETING_RANGE_PRESETS.map(p => (
            <div key={p.value} className={`page-tab${datePreset === p.value ? ' active' : ''}`} onClick={() => setDatePreset(p.value)}>
              {p.label}
            </div>
          ))}
        </div>
        {datePreset === 'custom' && (
          <MarketingDateRangePicker start={customStart} end={customEnd} max={today()} onChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }} />
        )}
      </div>

      {/* ── Filter Grid ─────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end',
        padding: '14px 16px',
        background: 'var(--surface)', borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border2)', marginBottom: 12,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>账号</label>
          <MultiSelect options={accountOptions} value={selectedAccounts} onChange={setSelectedAccounts} placeholder="全部账号" searchable />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 130 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>细分维度</label>
          <MultiSelect single options={DIMENSION_OPTIONS} value={dimension ? [dimension] : ['']} onChange={v => setDimension(v[0] ?? '')} placeholder="不细分" />
        </div>

        {tagFilters.map(group => mkFilter(
          group.label,
          tagGroupToOpts(group),
          tagFilterValues[group.key] || '',
          v => setTagFilterValues(prev => ({ ...prev, [group.key]: v })),
          group.key === 'funnel_detail' ? 200 : group.key === 'ad_format_tag' ? 160 : group.key === 'creative_type' ? 130 : 120,
        ))}
      </div>

      {/* ── Search & Fields Row ──────────────────────────────────────── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end',
        padding: '12px 16px', background: 'var(--surface)', borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border2)', marginBottom: 12,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>广告ID</label>
          <input className="form-input" placeholder="精确匹配" value={adId} onChange={e => setAdId(e.target.value)} style={{ width: 140, height: 34 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>广告名称</label>
          <input className="form-input" placeholder="模糊搜索" value={adName} onChange={e => setAdName(e.target.value)} style={{ width: 160, height: 34 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>系列名称</label>
          <input className="form-input" placeholder="模糊搜索" value={campaignName} onChange={e => setCampaignName(e.target.value)} style={{ width: 160, height: 34 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>广告组名称</label>
          <input className="form-input" placeholder="模糊搜索" value={adGroupName} onChange={e => setAdGroupName(e.target.value)} style={{ width: 160, height: 34 }} />
        </div>
      </div>

      {/* ── Info bar ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '0 0 8px', fontSize: 12, color: 'var(--text3)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>共 <b style={{ color: 'var(--text)' }}>{total}</b> 条</span>
          {hasDimension && <span style={{ opacity: .5 }}>|</span>}
          {hasDimension && <span>细分: {dimLabel}</span>}
          {hasAccount && <span style={{ opacity: .5 }}>|</span>}
          {hasAccount && <span>已选 {selectedAccounts.length} 个账号</span>}
        </div>
        <ColumnToggle cols={metricToggleCols} hiddenKeys={hiddenMetricKeys} onToggle={toggleMetricHidden} onReset={resetMetricHidden} />
      </div>

      {/* ── Table ────────────────────────────────────────────────────── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
        <div className="table-wrap" style={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
          <table style={{ minWidth: 600 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <th style={{ position: 'sticky', left: 0, zIndex: 4, background: 'var(--surface2)' }}>日期</th>
                {hasAccount && <th>账号</th>}
                {hasDimension && <th>{dimLabel}</th>}
                {activeMetrics.map(m => <th key={m.key as string} style={{ textAlign: 'right' }}>{m.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {summary && (
                <tr style={{ background: '#fffbeb', fontWeight: 600, fontSize: 12 }}>
                  <td style={{ position: 'sticky', left: 0, zIndex: 1, background: '#fffbeb', color: 'var(--text2)' }}>汇总</td>
                  {hasAccount && <td></td>}
                  {hasDimension && <td></td>}
                  {activeMetrics.map(m => {
                    const raw = m.fmt(summary[m.key] as number);
                    const display = m.permKey ? (sf(raw, m.permKey, userLevel).value ?? '🔒') : raw;
                    return (
                      <td key={m.key as string} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>
                        {display}
                      </td>
                    );
                  })}
                </tr>
              )}
              {loading && data.length === 0 ? (
                <tr><td colSpan={99}><TableLoadingCell /></td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={99} style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>暂无数据</td></tr>
              ) : data.map((row, idx) => {
                const isDateGroupEnd = idx === data.length - 1 || data[idx + 1]?.date !== row.date;
                const subtotal = isDateGroupEnd ? dailySubtotals.get(row.date) : undefined;

                return (
                  <React.Fragment key={idx}>
                    <tr>
                      {rowSpans.dateSpan[idx] > 0 && (
                        <td
                          rowSpan={rowSpans.dateSpan[idx]}
                          style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--surface)', fontWeight: 500, verticalAlign: 'top' }}
                        >
                          {row.date?.slice(0, 10)}
                        </td>
                      )}
                      {hasAccount && rowSpans.accountSpan[idx] > 0 && (
                        <td rowSpan={rowSpans.accountSpan[idx]} style={{ verticalAlign: 'top' }}>
                          {row.account_name || row.account_id}
                        </td>
                      )}
                      {hasAccount && !hasDimension && rowSpans.accountSpan[idx] === 0 && null}
                      {!hasAccount && false}
                      {hasDimension && <td>{row.dimension_value}</td>}
                      {activeMetrics.map(m => {
                        const raw = m.fmt(row[m.key] as number);
                        const display = m.permKey ? (sf(raw, m.permKey, userLevel).value ?? '🔒') : raw;
                        return (
                          <td key={m.key as string} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                    {subtotal && (
                      <tr style={{ background: 'rgba(18,16,184,.04)', fontWeight: 600, fontSize: 12 }}>
                        <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'rgba(18,16,184,.04)', color: 'var(--primary)' }}>小计</td>
                        {hasAccount && <td></td>}
                        {hasDimension && <td></td>}
                        {activeMetrics.map(m => (
                          <td key={m.key as string} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {m.fmt(subtotal[m.key] as number)}
                          </td>
                        ))}
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {loading && data.length > 0 && <TableLoadingOverlay />}
        </div>
      </div>

      {total > 0 && (
        <Pagination page={page} totalPages={totalPage} onChange={handlePageChange} total={`共 ${total} 条`} pageSize={pageSize} pageSizeOptions={[20, 50, 100]} onPageSizeChange={setPageSize} disabled={loading} style={{ marginTop: 12 }} />
      )}
    </div>
  );
}
